// importApi.js — single API client for the internal lead-importer backend.
// Routes follow docs/importer-spec.md §5 verbatim (base path /import).
// Every request carries an MSAL bearer token acquired via acquireTokenSilent.
import { useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { toast } from "react-toastify";
import { importApiRequest } from "../../authConfig";

// Spec base path is /import; overridable at build time if the reverse proxy
// ends up mounting the API elsewhere (e.g. /api/import).
const API_BASE = process.env.REACT_APP_IMPORT_API_BASE || "/import";

export class ImportApiError extends Error {
	constructor(message, status, detail) {
		super(message);
		this.name = "ImportApiError";
		this.status = status;
		this.detail = detail;
	}
}

export async function acquireAccessToken(instance, account) {
	if (!account) {
		throw new ImportApiError("No signed-in account. Please sign in again.", 401);
	}
	const request = { ...importApiRequest, account };
	try {
		const result = await instance.acquireTokenSilent(request);
		return result.accessToken;
	} catch (err) {
		if (err instanceof InteractionRequiredAuthError) {
			// Silent renewal failed — fall back to an interactive popup.
			const result = await instance.acquireTokenPopup(request);
			return result.accessToken;
		}
		throw err;
	}
}

async function request(
	instance,
	account,
	path,
	{ method = "GET", json, formData, quiet = false } = {}
) {
	const token = await acquireAccessToken(instance, account);
	const headers = { Authorization: `Bearer ${token}` };

	let body;
	if (formData) {
		body = formData; // browser sets the multipart boundary itself
	} else if (json !== undefined) {
		headers["Content-Type"] = "application/json";
		body = JSON.stringify(json);
	}

	let response;
	try {
		response = await fetch(`${API_BASE}${path}`, { method, headers, body });
	} catch (err) {
		throw new ImportApiError(
			`Network error calling ${path}: ${err.message}`,
			0
		);
	}

	if (response.status === 401 || response.status === 403) {
		if (!quiet) {
			toast.error("You are not authorized for imports.", {
				toastId: "import-unauthorized",
			});
		}
		throw new ImportApiError("Not authorized for imports", response.status);
	}

	if (!response.ok) {
		let detail = "";
		try {
			detail = await response.text();
		} catch {
			/* ignore */
		}
		// FastAPI errors usually look like {"detail": "..."}
		let message = detail;
		try {
			const parsed = JSON.parse(detail);
			if (parsed && parsed.detail) {
				message =
					typeof parsed.detail === "string"
						? parsed.detail
						: JSON.stringify(parsed.detail);
			}
		} catch {
			/* body was not JSON */
		}
		throw new ImportApiError(
			message || `${method} ${path} failed (HTTP ${response.status})`,
			response.status,
			detail
		);
	}

	if (response.status === 204) return null;
	return response.json();
}

// GET /import/batches/{id}/file — download the original CSV. Not a JSON
// call, so it bypasses request() and drives fetch()/blob() directly; mirrors
// components/downloadUtils.js's object-URL download pattern but carries the
// bearer token like every other importApi call.
async function downloadBatchFile(instance, account, batchId) {
	const token = await acquireAccessToken(instance, account);

	let response;
	try {
		response = await fetch(`${API_BASE}/batches/${batchId}/file`, {
			headers: { Authorization: `Bearer ${token}` },
		});
	} catch (err) {
		throw new ImportApiError(
			`Network error downloading batch file: ${err.message}`,
			0
		);
	}

	if (response.status === 401 || response.status === 403) {
		toast.error("You are not authorized for imports.", {
			toastId: "import-unauthorized",
		});
		throw new ImportApiError("Not authorized for imports", response.status);
	}

	if (!response.ok) {
		let detail = "";
		try {
			detail = await response.text();
		} catch {
			/* ignore */
		}
		let message = detail;
		try {
			const parsed = JSON.parse(detail);
			if (parsed && parsed.detail) {
				message =
					typeof parsed.detail === "string"
						? parsed.detail
						: JSON.stringify(parsed.detail);
			}
		} catch {
			/* body was not JSON */
		}
		throw new ImportApiError(
			message || `Download failed (HTTP ${response.status})`,
			response.status,
			detail
		);
	}

	const disposition = response.headers.get("content-disposition") || "";
	const match = /filename="?([^";]+)"?/i.exec(disposition);
	const filename = (match && match[1]) || `import_batch_${batchId}.csv`;

	const blob = await response.blob();
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

// Authenticated file download for a GET route (e.g. the activity-log export).
async function downloadAuthed(instance, account, path, fallbackFilename) {
	const token = await acquireAccessToken(instance, account);
	let response;
	try {
		response = await fetch(`${API_BASE}${path}`, {
			headers: { Authorization: `Bearer ${token}` },
		});
	} catch (err) {
		throw new ImportApiError(`Network error downloading ${path}: ${err.message}`, 0);
	}
	if (!response.ok) {
		throw new ImportApiError(
			response.status === 403
				? "The activity log is restricted to admins."
				: `Download failed (HTTP ${response.status})`,
			response.status
		);
	}
	const disposition = response.headers.get("content-disposition") || "";
	const match = /filename="?([^";]+)"?/i.exec(disposition);
	const blob = await response.blob();
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = (match && match[1]) || fallbackFilename;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

// Content-Disposition → filename, including RFC 5987 `filename*=utf-8''…`
// (what FastAPI sends for names with spaces, e.g. "HARDI red.csv").
function filenameFrom(disposition, fallback) {
	const star = /filename\*=(?:utf-8|UTF-8)''([^;]+)/.exec(disposition || "");
	if (star) {
		try {
			return decodeURIComponent(star[1].trim());
		} catch {
			/* fall through */
		}
	}
	const plain = /filename="?([^";]+)"?/i.exec(disposition || "");
	return (plain && plain[1]) || fallback;
}

// GET a stored scrub list (red / yellow / green) as a browser download.
async function downloadScrubBucket(instance, account, resultId, bucket) {
	const token = await acquireAccessToken(instance, account);
	let response;
	try {
		response = await fetch(`${API_BASE}/scrub-results/${resultId}/${bucket}/file`, {
			headers: { Authorization: `Bearer ${token}` },
		});
	} catch (err) {
		throw new ImportApiError(`Network error downloading the ${bucket} list: ${err.message}`, 0);
	}
	if (!response.ok) {
		let message = `Download failed (HTTP ${response.status})`;
		try {
			message = (await response.json()).detail || message;
		} catch {
			/* not JSON */
		}
		throw new ImportApiError(message, response.status);
	}
	const blob = await response.blob();
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filenameFrom(response.headers.get("content-disposition"), `${bucket}.csv`);
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

function queryString(params) {
	const qs = new URLSearchParams();
	Object.entries(params || {}).forEach(([k, v]) => {
		if (v !== undefined && v !== null && String(v) !== "") qs.set(k, String(v));
	});
	const text = qs.toString();
	return text ? `?${text}` : "";
}

function buildApi(instance, account) {
	const call = (path, opts) => request(instance, account, path, opts);
	return {
		// POST /import/upload — multipart CSV
		uploadCsv(file) {
			const fd = new FormData();
			fd.append("file", file);
			return call("/upload", { method: "POST", formData: fd });
		},
		// GET /import/iris/fields
		getIrisFields: () => call("/iris/fields"),
		// GET /import/iris/options
		getIrisOptions: () => call("/iris/options"),
		// POST /import/preview
		preview: (payload) => call("/preview", { method: "POST", json: payload }),
		// POST /import/execute
		execute: (payload) => call("/execute", { method: "POST", json: payload }),
		// GET /import/batches
		listBatches: () => call("/batches"),
		// GET /import/batches/{id}
		getBatch: (batchId) => call(`/batches/${batchId}`),
		// GET /import/batches/{id}/file — triggers a browser download
		downloadBatchFile: (batchId) => downloadBatchFile(instance, account, batchId),
		// POST /import/batches/{id}/revert-scan
		revertScan: (batchId) =>
			call(`/batches/${batchId}/revert-scan`, { method: "POST", json: {} }),
		// POST /import/batches/{id}/revert
		revert: (batchId, decisions) =>
			call(`/batches/${batchId}/revert`, {
				method: "POST",
				json: { decisions },
			}),
		// GET /import/mappings
		listMappings: () => call("/mappings"),
		// POST /import/mappings
		saveMapping: (payload) =>
			call("/mappings", { method: "POST", json: payload }),
		// DELETE /import/mappings/{id}
		deleteMapping: (mappingId) =>
			call(`/mappings/${mappingId}`, { method: "DELETE" }),
		// POST /import/scrub-results/{id}/{bucket}/stage — a stored scrub list,
		// staged like an upload (same response as uploadCsv)
		stageScrubBucket: (resultId, bucket) =>
			call(`/scrub-results/${resultId}/${bucket}/stage`, { method: "POST", json: {} }),
		// GET /import/scrub-results/{id}/{bucket}/file — browser download
		downloadScrubBucket: (resultId, bucket) =>
			downloadScrubBucket(instance, account, resultId, bucket),
		// POST /import/contacts/preview — add contacts to existing leads (dry run)
		previewContacts: (uploadId) =>
			call("/contacts/preview", { method: "POST", json: { upload_id: uploadId } }),
		// POST /import/contacts/live-check — start the READ-ONLY live check
		startLiveCheck: (uploadId) =>
			call("/contacts/live-check", { method: "POST", json: { upload_id: uploadId } }),
		// GET /import/contacts/live-check/{upload_id} — progress, running totals, result
		getLiveCheck: (uploadId) =>
			call(`/contacts/live-check/${uploadId}?show_leads=25`),
		// POST /import/contacts/execute — start the add-contacts batch
		executeContacts: (uploadId) =>
			call("/contacts/execute", {
				method: "POST",
				json: { upload_id: uploadId, confirm: true },
			}),
		// POST /import/batches/{id}/resume — finish a failed add-contacts batch
		resumeBatch: (batchId) =>
			call(`/batches/${batchId}/resume`, { method: "POST", json: {} }),
		// GET /import/me — {email, is_admin}
		// {quiet: true} = no "not authorized" toast (used as an access probe)
		getMe: (opts) => call("/me", opts),
		// GET /import/audit — admin-only activity log (newest first)
		listAudit: (params) => call(`/audit${queryString(params)}`),
		// GET /import/audit/export — the same events as a .jsonl download
		exportAudit: (params) =>
			downloadAuthed(instance, account, `/audit/export${queryString(params)}`,
				"leadscrub-activity.jsonl"),
	};
}

// Hook: returns a stable API object bound to the signed-in MSAL account.
export function useImportApi() {
	const { instance, accounts } = useMsal();
	const account = accounts[0] || null;
	return useMemo(() => buildApi(instance, account), [instance, account]);
}
